import { useChipHandlers } from "@features/studio/use-chip-handlers";
import { useEffectPlease } from "@features/utils";
import { XMarkIcon } from "@heroicons/react/16/solid";
import { AnimatePresence } from "framer-motion";
import React, { useCallback, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface ChipMarker {
  id: string;
  number: number;
  x: number; // Position as percentage (0-1)
  y: number; // Position as percentage (0-1)
}

interface ChipMarkersProps {
  width: number;
  height: number;
  chips: ChipMarker[];
  maxChips?: number;
  active: boolean;
  onAddChip?: (chip: ChipMarker) => void;
  onMoveChip?: (chipId: string, x: number, y: number) => void;
  onSelectChip?: (chipId: string | null) => void;
}

export const ChipMarkers: React.FC<ChipMarkersProps> = ({
  width,
  height,
  chips,
  maxChips = 4,
  active,
  onAddChip,
  onMoveChip,
  onSelectChip,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingChipId, setDraggingChipId] = useState<string | null>(null);
  const { handleRemoveChip } = useChipHandlers();

  // Get position from mouse/touch event
  const getPosition = useCallback(
    (
      event: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent
    ): { x: number; y: number } => {
      const container = containerRef.current;
      if (!container) return { x: 0, y: 0 };

      const rect = container.getBoundingClientRect();

      let clientX: number, clientY: number;
      if ("touches" in event) {
        const touch = event.touches[0] || event.changedTouches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      // Return position as percentage (0-1)
      return {
        x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
        y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
      };
    },
    []
  );

  // Handle click to add new chip
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (!active || chips.length >= maxChips) return;

      // Check if we clicked on an existing chip
      const target = event.target as HTMLElement;
      if (target.closest(".chip-marker")) return;

      const pos = getPosition(event);
      const newChip: ChipMarker = {
        id: `chip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        number: chips.length + 1,
        x: pos.x,
        y: pos.y,
      };

      onAddChip?.(newChip);
      onSelectChip?.(newChip.id);
    },
    [active, chips.length, maxChips, getPosition, onAddChip, onSelectChip]
  );

  // Handle chip drag start
  const handleDragStart = useCallback(
    (chipId: string, event: React.MouseEvent | React.TouchEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setDraggingChipId(chipId);
      onSelectChip?.(chipId);
    },
    [onSelectChip]
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!draggingChipId) return;
      const pos = getPosition(event);
      onMoveChip?.(draggingChipId, pos.x, pos.y);
    },
    [draggingChipId, getPosition, onMoveChip]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggingChipId(null);
  }, []);

  // Attach global mouse events when dragging
  useEffectPlease(() => {
    if (draggingChipId) {
      const handleGlobalMouseMove = (e: MouseEvent) => handleDragMove(e);
      const handleGlobalMouseUp = () => handleDragEnd();
      const handleGlobalTouchMove = (e: TouchEvent) => handleDragMove(e);
      const handleGlobalTouchEnd = () => handleDragEnd();

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove);
      document.addEventListener("touchend", handleGlobalTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [draggingChipId, handleDragMove, handleDragEnd]);

  if (!active && chips.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "absolute inset-0 z-30",
        active && chips.length < maxChips
          ? "cursor-crosshair"
          : "cursor-default"
      )}
      style={{
        pointerEvents: active ? "auto" : "none",
        width,
        height,
      }}
      onClick={handleClick}
    >
      <AnimatePresence>
        {chips.map((chip) => (
          <div
            key={chip.id}
            className={twMerge(
              "chip-marker absolute transform -translate-x-1/2 -translate-y-1/2 group",
              draggingChipId === chip.id && "z-50"
            )}
            style={{
              left: `${chip.x * 100}%`,
              top: `${chip.y * 100}%`,
              pointerEvents: "auto",
            }}
          >
            {/* Chip number badge */}
            <div
              className={twMerge(
                "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm cursor-move shadow-lg transition-all duration-150",
                "bg-blue-600 ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110"
              )}
              onMouseDown={(e) => handleDragStart(chip.id, e)}
              onTouchStart={(e) => handleDragStart(chip.id, e)}
              onClick={(e) => {
                e.stopPropagation();
                onSelectChip?.(chip.id);
              }}
            >
              {chip.number}
            </div>

            {/* Remove button - shown on hover or when selected */}
            {active && (
              <button
                className={twMerge(
                  "absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-opacity duration-150",
                  "opacity-0 group-hover:opacity-100"
                )}
                onClick={(e) => handleRemoveChip(chip.id)}
              >
                <XMarkIcon className="w-3 h-3 text-white" />
              </button>
            )}

            {/* Pulse animation for active chips */}
            {active && (
              <div className="absolute inset-0 w-8 h-8 rounded-full bg-blue-400 animate-ping opacity-30 -z-10" />
            )}
          </div>
        ))}
      </AnimatePresence>

      {/* Helper text when chip mode is active but no chips yet */}
      {active && chips.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/60 text-white px-4 py-2 rounded-lg text-base">
            Click on something you want to edit (max {maxChips})
          </div>
        </div>
      )}

      {/* Indicator showing remaining chips */}
      {active && chips.length > 0 && chips.length < maxChips && (
        <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs pointer-events-none">
          {chips.length}/{maxChips} markers
        </div>
      )}
    </div>
  );
};

// Hook for managing chip state
export const useChipMarkers = (initialChips: ChipMarker[] = []) => {
  const [chips, setChips] = useState<ChipMarker[]>(initialChips);
  const [selectedChipId, setSelectedChipId] = useState<string | null>(null);

  const addChip = useCallback((chip: ChipMarker) => {
    setChips((prev) => [...prev, chip]);
  }, []);

  const removeChip = useCallback((chipId: string) => {
    setChips((prev) => {
      const filtered = prev.filter((c) => c.id !== chipId);
      // Renumber remaining chips
      return filtered.map((chip, index) => ({
        ...chip,
        number: index + 1,
      }));
    });
    setSelectedChipId((prev) => (prev === chipId ? null : prev));
  }, []);

  const moveChip = useCallback((chipId: string, x: number, y: number) => {
    setChips((prev) =>
      prev.map((chip) => (chip.id === chipId ? { ...chip, x, y } : chip))
    );
  }, []);

  const clearChips = useCallback(() => {
    setChips([]);
    setSelectedChipId(null);
  }, []);

  return {
    chips,
    setChips,
    selectedChipId,
    setSelectedChipId,
    addChip,
    removeChip,
    moveChip,
    clearChips,
  };
};
